import { PublicClientApplication } from "@azure/msal-browser";
import { AxiosError } from "axios";
import { QueryClient } from "react-query";

import { RefreshTokenErrorMessage } from "./variables";

const AppSingletonFactory = (() => {
  let isSetup = false;
  let instance: {
    apiBaseUrl?: string;
    queryClient?: QueryClient;
    msal?: PublicClientApplication;
    muiLicenseKey?: string;
    sendDateTimeHours: number;
    environment?: "development" | "staging" | "uat" | "production";
    sandboxUrl?: string;
    sandboxUrlLoginRedirect?: string;
    domainSharing?: string;
  } = {
    sendDateTimeHours: 0,
  };

  function createInstance() {
    isSetup = true;
    const sendDateTimeHours = Number(
      process.env.REACT_APP_SEND_DATE_TIME_HOURS
    );
    const object = Object.defineProperties(instance, {
      apiBaseUrl: {
        value: process.env.REACT_APP_API_BASE_URL,
        writable: false,
        enumerable: false,
        configurable: false,
      },
      queryClient: {
        value: new QueryClient({
          defaultOptions: {
            queries: {
              refetchOnWindowFocus: false,
              retry: (_failureCount, error) => {
                const axiosErrorResponseStatus = (error as AxiosError)?.response
                  ?.status;
                const errorMessage = (error as Error).message;
                // The request will retry when it gets a status of "500" or more and is not related to the "refreshToken" error
                return (
                  !!axiosErrorResponseStatus &&
                  axiosErrorResponseStatus >= 500 &&
                  errorMessage !== RefreshTokenErrorMessage
                );
              },
            },
          },
        }),
        writable: false,
        enumerable: false,
        configurable: false,
      },
      msal: {
        value: new PublicClientApplication({
          auth: {
            clientId: process.env.REACT_APP_MSAL_CLIENT_ID as string,
            authority: process.env.REACT_APP_MSAL_AUTHORITY as string,
            redirectUri: process.env.REACT_APP_MSAL_REDIRECT_URI as string,
            postLogoutRedirectUri: process.env
              .REACT_APP_POST_LOGOUT_REDIRECT_URI as string,
          },
          cache: {
            cacheLocation: "sessionStorage", // This configures where your cache will be stored
            storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
          },
        }),
        writable: false,
        enumerable: false,
        configurable: false,
      },
      muiLicenseKey: {
        value: process.env.REACT_APP_MUI_LICENSE_KEY,
        writable: false,
        enumerable: false,
        configurable: false,
      },
      sendDateTimeHours: {
        value:
          typeof sendDateTimeHours === "number" &&
          !Number.isNaN(sendDateTimeHours)
            ? sendDateTimeHours
            : 0,
        writable: false,
        enumerable: false,
        configurable: false,
      },
      environment: {
        value: process.env.REACT_APP_ENVIRONMENT,
        writable: false,
        enumerable: false,
        configurable: false,
      },
      sandboxUrl: {
        value: process.env.REACT_APP_SANDBOX_URL,
        writable: false,
        enumerable: false,
        configurable: false,
      },
      sandboxUrlLoginRedirect: {
        value: process.env.REACT_APP_SANDBOX_URL_LOGIN_REDIRECT,
        writable: false,
        enumerable: false,
        configurable: false,
      },
      domainSharing: {
        value: process.env.REACT_APP_DOMAIN_SHARING,
        writable: false,
        enumerable: false,
        configurable: false,
      },
    });
    return object;
  }

  return {
    getInstance() {
      if (!isSetup) {
        instance = createInstance();
      }
      return instance;
    },
  };
})();

const AppInstance = AppSingletonFactory.getInstance();
export const ApiBaseUrl = AppInstance.apiBaseUrl;
export const QueryClientGlobal = AppInstance.queryClient;
export const MSALGlobal =
  AppInstance.msal as unknown as PublicClientApplication;
export const MUILicenseKey = AppInstance.muiLicenseKey;
export const SendDateTimeHours = AppInstance.sendDateTimeHours;
export const Environment = AppInstance.environment;
export const SandboxUrl = AppInstance.sandboxUrl;
export const SandboxUrlLoginRedirect = AppInstance.sandboxUrlLoginRedirect;
export const DomainSharing = AppInstance.domainSharing;
